import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText } from "lucide-react";
import api from "../lib/api";

/** Header search with debounced autosuggest dropdown. */
export default function SearchAutosuggest({ className = "" }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setItems([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/courses?q=${encodeURIComponent(q)}&limit=6`);
        setItems(data);
      } catch {
        setItems([]);
      }
    }, 180);
    return () => clearTimeout(timer.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submit = (e) => {
    e?.preventDefault?.();
    if (items[active]) {
      navigate(`/courses/${items[active].slug}`);
    } else if (q.trim()) {
      navigate(`/marketplace?q=${encodeURIComponent(q.trim())}`);
    }
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(items.length - 1, a + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    if (e.key === "Enter") submit(e);
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <form onSubmit={submit} ref={ref} className={`relative ${className}`} data-testid="header-search-form">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => q && setOpen(true)}
        onKeyDown={onKey}
        placeholder="Search certifications…"
        className="h-10 w-56 xl:w-64 rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-foreground transition"
        data-testid="header-search-input"
      />
      {open && items.length > 0 && (
        <div className="absolute top-full mt-2 left-0 right-0 rounded-lg border border-border bg-popover shadow-soft overflow-hidden z-50" data-testid="autosuggest-dropdown">
          {items.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => { navigate(`/courses/${c.slug}`); setOpen(false); }}
              className={`w-full text-left flex items-center gap-3 px-3 py-2 text-sm ${
                i === active ? "bg-accent" : "hover:bg-accent"
              }`}
              data-testid={`autosuggest-item-${i}`}
            >
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.title}</div>
                <div className="text-xs text-muted-foreground">{c.vendor} · ${c.price.toFixed(2)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
